import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Download, ExternalLink, ImageOff } from 'lucide-react'
import { Badge, cn, Markdown } from '@ludus/ui'
import type {
  LeadDetail,
  LeadDetailLink,
  LeadDetailSection,
  LeadId,
  LeadMediaRef,
  PipelineId,
} from '@ludus/core'
import { fetchLeadMediaObjectUrl } from '@/lib/api/lead-detail.ts'

/**
 * Renders whatever the source said about this lead — and nothing more.
 *
 * There is no branch on `key` anywhere in this file, no list of known section
 * titles, and no source name. That is the point: the tecraft order archive fills
 * this with crystals, photos, engraving geometry and delivery, and a second
 * source will fill it with something else entirely, without either one needing a
 * component here. The adapter already wrote every label for a person to read
 * (`packages/core/src/adapters/detail.ts`); this only decides how deep a heading
 * sits and how a file is shown.
 *
 * Media comes through the engine's authed proxy rather than a direct link, so
 * the source's credential never reaches the browser — which is also why a
 * thumbnail is a blob fetch and not an `<img src>`.
 */

interface SourceDetailTabProps {
  pipelineId: PipelineId
  leadId: LeadId
  detail: LeadDetail
}

export function SourceDetailTab({ pipelineId, leadId, detail }: SourceDetailTabProps) {
  const { t } = useTranslation()

  if (detail.sections.length === 0) {
    return (
      <EmptyState
        text={t('pages.leads.detail.source.empty', {
          defaultValue: 'The source has nothing more to show for this record.',
        })}
      />
    )
  }

  return (
    <div className="flex flex-col gap-5">
      {detail.sections.map((section) => (
        <Section
          key={section.key}
          section={section}
          depth={0}
          pipelineId={pipelineId}
          leadId={leadId}
        />
      ))}
    </div>
  )
}

/** High enough that a normal body is never folded; the fold is for outliers. */
const PROSE_CAP_PX = 700

/**
 * Folded rather than given its own scrollbar: this panel already sits inside the
 * page's scroll, and a box that steals the wheel mid-document is the worse of
 * the two failures.
 */
function Prose({ text }: { text: string }) {
  const { t } = useTranslation()
  const content = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | null>(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    const el = content.current
    if (el === null) return

    const measure = () => setHeight(el.scrollHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text])

  const overflows = height !== null && height > PROSE_CAP_PX

  return (
    <div className="overflow-hidden rounded-lg border border-border/60 bg-background-secondary">
      <div
        className="relative overflow-hidden transition-[max-height] duration-300 ease-out motion-reduce:transition-none"
        // A measured pixel height, because no browser transitions to `auto`.
        style={{ maxHeight: expanded && height !== null ? height : PROSE_CAP_PX }}
      >
        <div ref={content} className="px-4 py-3.5">
          <Markdown>{text}</Markdown>
        </div>
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background-secondary to-transparent transition-opacity duration-300 motion-reduce:transition-none',
            overflows && !expanded ? 'opacity-100' : 'opacity-0',
          )}
        />
      </div>

      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="flex w-full items-center justify-center gap-1.5 border-t border-border/60 px-4 py-2 text-xs font-medium text-muted transition-colors hover:bg-background/40 hover:text-foreground"
        >
          {expanded
            ? t('pages.leads.detail.source.showLess', { defaultValue: 'Collapse' })
            : t('pages.leads.detail.source.showMore', { defaultValue: 'Show the rest' })}
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-3.5 transition-transform duration-300 motion-reduce:transition-none',
              expanded && 'rotate-180',
            )}
          />
        </button>
      )}
    </div>
  )
}

/**
 * Depth drives typography and nothing else — a section three levels down is
 * still rows and files, so the recursion has one shape rather than a component
 * per level. Beyond depth 2 the styling stops changing; a document that deep is
 * already telling the operator more than it should.
 */
function Section({
  section,
  depth,
  pipelineId,
  leadId,
}: {
  section: LeadDetailSection
  depth: number
  pipelineId: PipelineId
  leadId: LeadId
}) {
  const rows = section.rows ?? []
  const media = section.media ?? []
  const links = (section.links ?? []).filter((l) => isSafeHref(l.url))
  const children = section.sections ?? []

  return (
    <section
      className={cn(
        // The media grid below sizes itself off this section, not the viewport
        // (apps/console/CLAUDE.md, "container queries first"). Without the
        // container declared here the `@sm:` variant matches nothing at all and
        // the grid silently stays one column — which on a wide panel renders one
        // square thumbnail per screen.
        '@container flex flex-col gap-2.5',
        depth > 0 && 'rounded-lg border border-border/60 bg-background p-3',
      )}
    >
      <header className="flex flex-wrap items-center gap-2">
        <h3
          className={cn(
            depth === 0 && 'text-xs font-semibold tracking-wide uppercase text-muted',
            depth >= 1 && 'text-sm font-semibold text-foreground',
          )}
        >
          {section.title}
        </h3>
        {section.subtitle != null && section.subtitle !== '' && (
          <span className="text-[11px] text-muted">{section.subtitle}</span>
        )}
        {links.length > 0 && (
          // In the heading: at the foot of a long body nobody reaches it.
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {links.map((link) => (
              <SourceLink key={link.url} link={link} />
            ))}
          </div>
        )}
      </header>

      {section.text != null && section.text !== '' && <Prose text={section.text} />}

      {rows.length > 0 && (
        // Stacked pairs, not a label column: this pane is twice the width of
        // the properties panel opposite, where a table would strand each value
        // a corridor away from its own label.
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 @md:grid-cols-2 @3xl:grid-cols-3">
          {rows.map((row, i) => (
            <div
              key={`${row.label}-${i}`}
              className="flex min-w-0 flex-col gap-0.5 border-l-2 border-border/50 pl-2.5"
            >
              <dt className="text-[10px] font-semibold tracking-[0.12em] uppercase text-muted">
                {row.label}
              </dt>
              <dd
                className={cn(
                  'min-w-0 break-words text-foreground',
                  row.mono === true ? 'font-mono text-[13px]' : 'text-sm',
                )}
              >
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {media.length > 0 && (
        <div className="grid grid-cols-1 gap-3 @sm:grid-cols-2 @2xl:grid-cols-3">
          {media.map((file) => (
            <MediaCard key={file.id} file={file} pipelineId={pipelineId} leadId={leadId} />
          ))}
        </div>
      )}

      {children.map((child) => (
        <Section
          key={child.key}
          section={child}
          depth={depth + 1}
          pipelineId={pipelineId}
          leadId={leadId}
        />
      ))}
    </section>
  )
}

/**
 * A way out to the source's own UI, rendered from what the adapter wrote. The
 * console does not know where it goes — only that it leaves.
 *
 * `noopener` because a `_blank` target otherwise hands the opened page a live
 * `window.opener` back into the console, and `noreferrer` keeps our URLs out of
 * that system's logs for the same reason the preview frame does.
 */
function SourceLink({ link }: { link: LeadDetailLink }) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      title={link.hint}
      className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background-secondary px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors outline-none hover:border-primary/50 hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
    >
      <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {link.label}
    </a>
  )
}

/**
 * The adapter is trusted, but this string becomes an `href`. A `javascript:`
 * URL there executes in the console's own origin, so the scheme is checked for
 * the same reason `SourceEmbedTab` checks the one it frames.
 */
function isSafeHref(url: string): boolean {
  try {
    const { protocol } = new URL(url)
    return protocol === 'https:' || protocol === 'http:'
  } catch {
    return false
  }
}

function MediaCard({
  file,
  pipelineId,
  leadId,
}: {
  file: LeadMediaRef
  pipelineId: PipelineId
  leadId: LeadId
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border/60 bg-background p-2">
      <div className="flex items-center justify-between gap-2">
        {file.label != null && file.label !== '' ? (
          <Badge tone="muted" size="sm">
            {file.label}
          </Badge>
        ) : (
          <span />
        )}
        <DownloadButton file={file} pipelineId={pipelineId} leadId={leadId} />
      </div>

      <MediaThumb file={file} pipelineId={pipelineId} leadId={leadId} />

      <div className="flex flex-col">
        <span className="truncate text-xs text-foreground" title={file.fileName ?? undefined}>
          {file.fileName ?? file.id}
        </span>
        {file.byteSize != null && (
          <span className="text-[11px] text-muted">{formatBytes(file.byteSize)}</span>
        )}
      </div>
    </div>
  )
}

/**
 * Bearer-authed blob → object URL, revoked on unmount and on every id change.
 * A blob nobody revokes keeps its bytes for the life of the document, and a
 * spec sheet with a dozen photos on it is exactly where that adds up.
 */
function MediaThumb({
  file,
  pipelineId,
  leadId,
}: {
  file: LeadMediaRef
  pipelineId: PipelineId
  leadId: LeadId
}) {
  const { t } = useTranslation()
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const held = useRef<string | null>(null)

  useEffect(() => {
    let alive = true
    setFailed(false)
    setUrl(null)

    fetchLeadMediaObjectUrl(pipelineId, leadId, file.id, 400)
      .then((objectUrl) => {
        // Losing the race still allocated a blob, so it is released here rather
        // than left to the cleanup that will never see it.
        if (!alive) {
          URL.revokeObjectURL(objectUrl)
          return
        }
        held.current = objectUrl
        setUrl(objectUrl)
      })
      .catch(() => {
        if (alive) setFailed(true)
      })

    return () => {
      alive = false
      if (held.current !== null) {
        URL.revokeObjectURL(held.current)
        held.current = null
      }
    }
  }, [pipelineId, leadId, file.id])

  const isImage = file.contentType == null || file.contentType.startsWith('image/')
  if (!isImage) return null

  return (
    // Capped as well as square: a spec photo is something an operator scans on
    // the way to the numbers under it, so it must not take a screen on its own
    // if the grid ever falls back to one column.
    <div className="flex aspect-square max-h-64 items-center justify-center overflow-hidden rounded-md border border-border/60 bg-background-secondary">
      {failed ? (
        <ImageOff className="h-5 w-5 text-muted" aria-hidden="true" />
      ) : url !== null ? (
        <img
          src={url}
          alt={file.label ?? file.fileName ?? ''}
          className="h-full w-full object-contain"
        />
      ) : (
        <span className="text-[11px] text-muted/60">
          {t('pages.leads.detail.source.loadingFile', { defaultValue: '…' })}
        </span>
      )}
    </div>
  )
}

/** Full resolution, fetched on click — the grid never pays for it. */
function DownloadButton({
  file,
  pipelineId,
  leadId,
}: {
  file: LeadMediaRef
  pipelineId: PipelineId
  leadId: LeadId
}) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)

  const download = async () => {
    if (busy) return
    setBusy(true)
    let objectUrl: string | null = null
    try {
      objectUrl = await fetchLeadMediaObjectUrl(pipelineId, leadId, file.id)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = file.fileName ?? file.id
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    } finally {
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl)
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      onClick={() => void download()}
      disabled={busy}
      aria-label={t('pages.leads.detail.source.download', { defaultValue: 'Download' })}
      className="inline-flex h-6 w-6 items-center justify-center rounded text-muted outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
    >
      <Download className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  )
}

export function EmptyState({ text, tone }: { text: string; tone?: 'error' }) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border border-border/40 bg-background-secondary/40 px-4 py-3 text-xs',
        tone === 'error' ? 'text-toast-error-text' : 'text-muted',
      )}
    >
      {text}
    </div>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
